/**
 * Timeline data transformation utilities
 *
 * Functions to transform raw data from stores/APIs into timeline event formats
 */

import { format } from "date-fns";
import { Reminder } from '@/bryntum-lib/models/Reminder';
import { Task } from '@/bryntum-lib/models/Task';
import { Client } from '@/bryntum-lib/models/Client';
import { Resource as ResourceModel } from '@/bryntum-lib/models/Resource';
import { Event } from '@/bryntum-lib/models/Event';
import { ISwivuProject } from "@/types/SwivuProject";
import { pluralize } from "@/other/utils/otherUtils";
import {
  StatusChange,
  HistoryEvent,
  ScheduleEvent,
  PlanningEvent,
  ReminderEvent,
  ResourceDetail,
  EntityRecord
} from './types';

/**
 * Transforms task status history API response to StatusChange format
 */
export const transformStatusHistory = (statusHistoryData: Record<string, unknown>[]): StatusChange[] => {
  return statusHistoryData.map((item: Record<string, unknown>) => ({
    label: (item.status as string) || "N/A",
    date: format(new Date(item.history_date as string), "yyyy-MM-dd"),
    updatedBy: (item.history_user_name as string) || undefined
  }));
};

/**
 * Transforms task events to ScheduleEvent format
 */
export const transformScheduleDates = (events: Record<string, unknown>[]): ScheduleEvent[] => {
  const scheduleMap = new Map<string, { resources: string[]; originalEvents: Record<string, unknown>[] }>();

  events.forEach((event: Record<string, unknown>) => {
    if (event.resource) {
      const dateKey = format(new Date(event.startDate as string), "yyyy-MM-dd");
      const resourceName = ((event.resource as Record<string, unknown>).name as string) || "Unknown Resource";

      if (!scheduleMap.has(dateKey)) {
        scheduleMap.set(dateKey, { resources: [], originalEvents: [] });
      }
      const dateData = scheduleMap.get(dateKey)!;
      dateData.resources.push(resourceName);
      dateData.originalEvents.push(event);
    }
  });

  return Array.from(scheduleMap.entries()).map(([date, data]) => ({
    date,
    resources: [...new Set(data.resources)], // Remove duplicates
    originalEvents: data.originalEvents
  }));
};

/**
 * Transforms task planning dates to PlanningEvent format
 */
export const transformPlanningDates = (planningDatesRecords: Record<string, unknown>[]): PlanningEvent[] => {
  return planningDatesRecords.map((record: Record<string, unknown>) => {
    const startDate = record.startDate;
    const endDate = record.endDate;

    // Ensure dates are valid strings or Date objects
    const startDateStr = startDate instanceof Date ? startDate.toISOString() : String(startDate || "");
    const endDateStr = endDate instanceof Date ? endDate.toISOString() : String(endDate || "");

    // Calculate duration in days if both dates are valid
    let duration = "Duration not available";
    if (startDateStr && endDateStr && startDateStr !== "undefined" && endDateStr !== "undefined") {
      try {
        const start = new Date(startDateStr);
        const end = new Date(endDateStr);
        if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
          const diffTime = Math.abs(end.getTime() - start.getTime());
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end days
          duration = `${diffDays} day${diffDays !== 1 ? "s" : ""}`;
        }
      } catch (error) {
        console.warn("Error calculating duration for planning date:", error);
      }
    }

    return {
      id: (record.id as string) || undefined,
      date: startDateStr ? format(new Date(startDateStr), "yyyy-MM-dd") : "Unknown date",
      color: (record.color as string) || "#ef4444",
      label: (record.name as string) || "Planning Event",
      startDate: startDateStr,
      endDate: endDateStr,
      duration
    };
  });
};

/**
 * Transforms reminders to ReminderEvent format
 */
export const transformReminderDates = (reminders: Reminder[], entityRecord: EntityRecord): ReminderEvent[] => {
  return reminders.map((reminder: Reminder) => {
    const recurrenceMap: { [key: number]: string } = {
      0: "Never",
      1: "Daily",
      2: "Weekly",
      3: "Monthly",
      4: "Yearly"
    };

    // Get notify users based on entity type
    let notifyUsers: string[] = [];
    if ('reminderNotifyUsers' in entityRecord) {
      notifyUsers = entityRecord.reminderNotifyUsers || [];
    }

    return {
      id: (reminder.id as string) || undefined,
      date: format(new Date(reminder.alertDate as Date), "yyyy-MM-dd"),
      alertDate: reminder.alertDate.toISOString(),
      label: (reminder.title as string) || (reminder.description as string) || "Reminder",
      recurring: Boolean(reminder.recurringInterval),
      recurrence: (recurrenceMap[reminder.recurringInterval as number] || "Never") as "Never" | "Daily" | "Weekly" | "Monthly" | "Yearly",
      repeatCount: (reminder.repeatCount as number) || undefined,
      notifies: notifyUsers,
      creator: reminder.creatorBoardUserRecord?.name || "N/A",
      reminder
    };
  });
};

/**
 * Transforms task history for Client entities
 * Inspired by ClientsTaskHistoryTab.jsx
 */
const getClientSchedulerEvents = (project: ISwivuProject, client: Client): HistoryEvent[] => {
  const tasks = client.tasks;
  if (!tasks || tasks.length === 0) return [];

  return tasks.reduce((history: HistoryEvent[], task: Task) => {
    const events = project.eventStore.findByField("taskId", task.id);
    const taskEvents = events.map((event) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const eventData = event as any;
      const { startDate, endDate, duration, durationUnit } = eventData;
      return {
        date: format(new Date(startDate as Date), "yyyy-MM-dd"),
        taskName: task.name || "Unknown Task",
        taskColor: task.color,
        taskStyle: task.taskStyle,
        startDate: startDate as string,
        endDate: endDate as string,
        duration: `${duration || ""} ${durationUnit || ""}`,
        status: task.statusValue,
        task,
        originalEvent: eventData
      };
    });
    return history.concat(taskEvents);
  }, []);
};

/**
 * Transforms task history for Resource entities
 * Inspired by ResourceTaskHistoryTab.jsx
 */
const getResourceSchedulerEvents = (resource: ResourceModel): HistoryEvent[] => {
  const events = resource.events || [];
  if (events.length === 0) return [];

  return events.map((event: Event) => {
    const { name, task, startDate, endDate, duration, durationUnit } = event;
    return {
      date: format(new Date(startDate as Date), "yyyy-MM-dd"),
      taskName: task?.name || name || "Unknown Task",
      taskColor: task?.color,
      taskStyle: task?.taskStyle,
      startDate: startDate as string,
      endDate: endDate as string,
      duration: `${duration || ""} ${pluralize(duration, durationUnit) || ""}`,
      durationUnit,
      status: task?.statusValue,
      task,
      originalEvent: event
    };
  });
};

/**
 * Transforms history dates for Client or Resource entities
 */
export const transformHistoryDates = (
  record: EntityRecord,
  project: ISwivuProject
): HistoryEvent[] => {
  // Only for non-task entities
  if (record.entityType === 'taskId') return [];

  if (record.entityType === 'clientId') {
    return getClientSchedulerEvents(project, record as Client);
  } else if (record.entityType === 'resourceId') {
    return getResourceSchedulerEvents(record as ResourceModel);
  }

  return [];
};

/**
 * Transforms event and resource data to ResourceDetail format for modal display
 */
export const transformResourceDetails = (event: Record<string, unknown>, project: ISwivuProject): ResourceDetail[] => {
  const resourceStore = project.resourceStore;
  const resourceDetails: ResourceDetail[] = [];

  // Handle single resource assignment
  if (event.resource) {
    const resourceId = (event.resource as Record<string, unknown>).id as string;
    const resource = resourceStore.getById(resourceId);
    if (resource) {
      resourceDetails.push(createResourceDetail(resource, event));
    }
  }

  // Handle multiple resource assignments (taskEventResources)
  if (event.taskEventResources && Array.isArray(event.taskEventResources)) {
    event.taskEventResources.forEach((resourceId: string) => {
      const resource = resourceStore.getById(resourceId);
      if (resource) {
        resourceDetails.push(createResourceDetail(resource, event));
      }
    });
  }

  return resourceDetails;
};

/**
 * Creates a single ResourceDetail object from resource and event data
 */
const createResourceDetail = (resource: ResourceModel, event: Record<string, unknown>): ResourceDetail => {
  // Get primary contact information
  const primaryContact = getResourceContactInfo(resource);

  // Format timing information
  const timing = formatResourceTiming(event);

  return {
    name: (resource.name as string) || "Unknown Resource",
    description: (resource.description as string) || "No Description",
    phone: primaryContact.phone,
    email: primaryContact.email,
    startDate: timing.startDate,
    startTime: timing.startTime,
    endDate: timing.endDate,
    endTime: timing.endTime,
    duration: timing.duration
  };
};

/**
 * Extracts contact information from resource
 */
const getResourceContactInfo = (resource: Record<string, unknown>): { phone: string; email: string } => {
  const contacts = resource.contacts as Record<string, unknown>[];

  if (contacts && contacts.length > 0) {
    const primaryContact = contacts[0].contactRecord as Record<string, unknown>;
    return {
      phone: (primaryContact?.phone as string) || "N/A",
      email: (primaryContact?.email as string) || "N/A"
    };
  }

  return {
    phone: "N/A",
    email: "N/A"
  };
};

/**
 * Formats timing information from event data
 */
const formatResourceTiming = (
  event: Record<string, unknown>
): {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  duration: string;
} => {
  const startDate = new Date(event.startDate as string);
  const endDate = new Date(event.endDate as string);

  // Calculate duration in hours
  const durationMs = endDate.getTime() - startDate.getTime();
  const durationHours = Math.round(durationMs / (1000 * 60 * 60));

  return {
    startDate: format(startDate, "yyyy-MM-dd"),
    startTime: format(startDate, "HH:mm"),
    endDate: format(endDate, "yyyy-MM-dd"),
    endTime: format(endDate, "HH:mm"),
    duration: `${durationHours}h`
  };
};

/**
 * Extracts initials from a full name for avatar display
 */
export const extractInitials = (name: string): string => {
  // Handle existing initials (2-3 letter codes)
  if (name.length <= 3 && name === name.toUpperCase()) {
    return name;
  }

  // Extract initials from full names
  return name
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase())
    .join("")
    .substring(0, 2);
};

/**
 * Gets resource ID from event for deduplication
 */
export const getResourceIdFromEvent = (event: Record<string, unknown>): string | null => {
  if (event.resource) {
    return (event.resource as Record<string, unknown>).id as string;
  }
  return null;
};
